import React, { useEffect, useState, useRef, useLayoutEffect } from 'react';

const Content = () => {
  const [html, setHtml] = useState('');
  const contentRef = useRef(null);

  useEffect(() => {
    fetch('/content.html')
      .then(response => response.text())
      .then(data => {
        setHtml(data);
      })
      .catch(error => console.warn(error));
  }, []);

  // Hide loading overlay immediately after HTML is rendered
  useLayoutEffect(() => {
    if (html) {
      // Inject a style to force hide the overlay
      const style = document.createElement('style');
      style.textContent = `
        #loadingOverlay,
        .loading-overlay {
          display: none !important;
          opacity: 0 !important;
          visibility: hidden !important;
          pointer-events: none !important;
        }
      `;
      document.head.appendChild(style);

      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        const hideLoadingOverlay = () => {
          // Try to find overlay in the injected content
          const loadingOverlay = contentRef.current?.querySelector('#loadingOverlay');
          if (loadingOverlay) {
            loadingOverlay.classList.add('hidden');
            loadingOverlay.style.cssText = 'display: none !important; opacity: 0 !important; visibility: hidden !important;';
          }
          // Also try to find it in the main document
          const mainOverlay = document.getElementById('loadingOverlay');
          if (mainOverlay) {
            mainOverlay.classList.add('hidden');
            mainOverlay.style.cssText = 'display: none !important; opacity: 0 !important; visibility: hidden !important;';
          }
          // Also try querySelector on document
          const docOverlay = document.querySelector('#loadingOverlay');
          if (docOverlay) {
            docOverlay.classList.add('hidden');
            docOverlay.style.cssText = 'display: none !important; opacity: 0 !important; visibility: hidden !important;';
          }
          // Try class selector
          const classOverlays = document.querySelectorAll('.loading-overlay');
          classOverlays.forEach(overlay => {
            overlay.classList.add('hidden');
            overlay.style.cssText = 'display: none !important; opacity: 0 !important; visibility: hidden !important;';
          });
        };
        hideLoadingOverlay();
        // Also try after multiple delays to catch it
        setTimeout(hideLoadingOverlay, 10);
        setTimeout(hideLoadingOverlay, 50);
        setTimeout(hideLoadingOverlay, 100);
        setTimeout(hideLoadingOverlay, 300);
      });
    }
  }, [html]);

  useEffect(() => {
    if (html && contentRef.current) {

      // Extract and execute scripts from the HTML
      const scripts = contentRef.current.querySelectorAll('script');
      scripts.forEach((oldScript) => {
        const newScript = document.createElement('script');
        Array.from(oldScript.attributes).forEach((attr) => {
          newScript.setAttribute(attr.name, attr.value);
        });
        if (oldScript.src) {
          // External script - load it
          newScript.src = oldScript.src;
        } else {
          // Inline script - copy content
          newScript.textContent = oldScript.textContent;
        }
        oldScript.parentNode?.replaceChild(newScript, oldScript);
      });

      // Set up other interactive features
      const setupInteractivity = () => {
        // Navbar scroll effect
        window.addEventListener('scroll', function() {
          const navbar = contentRef.current?.querySelector('#navbar');
          if (navbar) {
            if (window.scrollY > 50) {
              navbar.classList.add('scrolled');
            } else {
              navbar.classList.remove('scrolled');
            }
          }
        });

        // Smooth scroll for anchor links
        contentRef.current?.querySelectorAll('a[href^="#"]').forEach(anchor => {
          anchor.addEventListener('click', function (e) {
            e.preventDefault();
            const target = contentRef.current?.querySelector(this.getAttribute('href'));
            if (target) {
              target.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
              });
            }
          });
        });

        // Scroll to top button
        const scrollToTopBtn = contentRef.current?.querySelector('#scrollToTop');
        if (scrollToTopBtn) {
          window.addEventListener('scroll', function() {
            if (window.scrollY > 300) {
              scrollToTopBtn.classList.add('visible');
            } else {
              scrollToTopBtn.classList.remove('visible');
            }
          });

          scrollToTopBtn.addEventListener('click', function() {
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
          });
        }

        // Product search functionality
        const productSearch = contentRef.current?.querySelector('#productSearch');
        const productGrid = contentRef.current?.querySelector('#productGrid');
        if (productSearch && productGrid) {
          const productCards = productGrid.querySelectorAll('[data-product-name]');
          
          productSearch.addEventListener('input', function(e) {
            const searchTerm = e.target.value.toLowerCase().trim();
            productCards.forEach(card => {
              const productName = card.getAttribute('data-product-name');
              if (productName && (productName.includes(searchTerm) || searchTerm === '')) {
                card.style.display = '';
                card.classList.add('fade-in');
              } else {
                card.style.display = 'none';
              }
            });
          });

          // Add stagger animation to product cards
          productCards.forEach((card, index) => {
            card.style.animationDelay = `${index * 0.1}s`;
          });
        }
      };

      // Wait a bit for DOM to be ready
      setTimeout(setupInteractivity, 100);
    }
  }, [html]);

  return <div ref={contentRef} dangerouslySetInnerHTML={{ __html: html }} />;
};

export default Content;
